<template>
  <div class="full-height">
    <div class="position--relative">
      <transition name="fade">
        <v-progress-linear
          v-if="isLoading"
          class="progress-linear--absolute"
          color="warning"
          height="3"
          indeterminate
        />
      </transition>
    </div>

    <transition name="fade">
      <div v-if="isSuccess" class="full-height">
        <div v-if="items.length" class="table">
          <div class="table__status">
            <strong>{{ firstItemNumber }}</strong>-<strong>{{ lastItemNumber}}</strong> {{ $t('Common.of') }} <strong>{{ pagination.totalElements | formatNumber }}</strong> {{ $t('Common.companies') }}
          </div>

          <v-layout class="table__header" align-center>
            <v-flex style="max-width: 38px;" xs1 />
            <v-flex xs1>ID</v-flex>
            <v-flex xs4>{{ $t('Common.socialReason') }}</v-flex>
            <v-flex xs3>{{ $t('Common.companyFantasyName') }}</v-flex>
            <v-flex xs2>CNPJ</v-flex>
            <v-flex v-if="isAdmin" xs2>Status</v-flex>
          </v-layout>

          <v-list subheader two-line class="table__body">
            <div v-for="(item, i) in items" :key="`tr-${i}`">
              <v-list-tile
                class="table__body__tr"
                @click="handleClick($event, item)"
              >
                <v-list-tile-content>
                  <v-list-tile-sub-title>
                    <v-layout align-center>
                      <v-flex style="max-width: 38px;" xs1>
                        <CompanyMenuActions :company="item" @reload="handleReloadData" />
                      </v-flex>
                      <v-flex xs1>
                        <span :title="item.id" class="text--ellipsis" contenteditable>
                          {{ item.id }}
                        </span>
                      </v-flex>
                      <v-flex xs4>
                        <span class="text--ellipsis" :title="item.name">
                          {{ item.name | ellipsis(40) }}
                        </span>
                      </v-flex>
                      <v-flex xs3>
                        <span class="text--ellipsis" :title="item.displayName">
                          {{ item.displayName | ellipsis(40) }}
                        </span>
                      </v-flex>
                      <v-flex xs2>
                        <span class="text--ellipsis" :title="item.documentId | cnpj">
                          {{ item.documentId | cnpj }}
                        </span>
                      </v-flex>
                      <v-flex v-if="isAdmin" xs2>
                        <StatusBullet :status="item.status" />
                      </v-flex>
                    </v-layout>
                  </v-list-tile-sub-title>
                </v-list-tile-content>
              </v-list-tile>

              <v-divider />
            </div>
          </v-list>
        </div>

        <EmptyState
          v-else
          image="cost-center"
          :text="$t(`${namespace}.noneFound`)"
          style="padding-bottom: 120px"
        />

        <div v-if="pagination.totalElements > pagination.itemsPerPage" class="text-xs-center">
          <v-container class="px-0">
            <v-pagination
              v-model="pagination.currentPage"
              color="accent"
              :total-visible="totalVisible"
              :length="pagination.totalPages"
              @input="handlePage"
            />
          </v-container>
        </div>
      </div>
    </transition>

    <transition name="fade">
      <v-layout v-if="isFailed" align-center justify-center column fill-height>
        {{ $t('Common.genericErrorMsg') }}<br>
        <a
          :title="$t('Common.tryAgain')"
          class="accent--text"
          style="margin-bottom: 120px"
          @click.prevent="handleReloadData"
        >
          {{ $t('Common.tryAgain') }}.
        </a>
      </v-layout>
    </transition>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { STATUS_IS_LOADING, STATUS_SUCCESS, STATUS_FAILED } from 'utils/constants'
import { formatNumber } from 'utils/functions'
import CompanyMenuActions from './CompanyMenuActions'
import EmptyState from 'components/EmptyState'
import StatusBullet from 'components/company/StatusBullet'

export default {
  name: 'Table',
  props: {
    items: {
      type: Array,
      required: true
    },
    pagination: {
      type: Object,
      required: true
    },
    status: {
      type: Number,
      default: STATUS_IS_LOADING
    }
  },
  components: {
    CompanyMenuActions,
    EmptyState,
    StatusBullet
  },
  computed: {
    ...mapState({
      selectedCompany: ({ company }) => company.selectedCompany,
      claims: ({ userClaims }) => userClaims.claims,
      isAdmin: state => {
        return state.session.user.business.isAdministrator ||
          state.userClaims.claims.profiles['ROLE_ADMINISTRATOR'] ||
          state.userClaims.claims.profiles['ROLE_FINANCIAL_ADMIN']
      }
    }),

    routeId () {
      const { id } = this.$route.params
      return id ? Number(id) : null
    },

    totalVisible () {
      return window.innerWidth <= 420 ? 5 : 7
    },

    firstItemNumber () {
      const { currentPage, itemsPerPage } = this.pagination
      const calc = (currentPage * itemsPerPage) - itemsPerPage + 1
      const item = currentPage === 1 ? 1 : calc
      return formatNumber(item)
    },

    lastItemNumber () {
      const { currentPage, itemsPerPage, totalElements } = this.pagination
      const calc = currentPage * itemsPerPage
      const item = calc > totalElements ? totalElements : calc
      return formatNumber(item)
    },

    isLoading () {
      return this.status === STATUS_IS_LOADING
    },

    isSuccess () {
      return this.status === STATUS_SUCCESS
    },

    isFailed () {
      return this.status === STATUS_FAILED
    }
  },
  data () {
    return {
      namespace: 'Views.CompanyPage.companies',
      selectedItem: null
    }
  },
  methods: {
    handlePage (page) {
      this.$emit('goToPage', page)
    },

    handleReloadData () {
      this.$emit('reloadData')
    },

    handleClick ($event, item) {
      const cls = $event.target.classList

      if (
        !cls.contains('v-btn__content') &&
        !cls.contains('v-icon') &&
        !cls.contains('v-input--selection-controls__ripple') &&
        !cls.contains('accent--text')
      ) {
        this.$router.push({
          name: 'CompanyDetail',
          params: { id: item.id }
        })
      }
    }
  }
}
</script>
